import { Component, OnInit, ViewChild } from '@angular/core';
import { ConsentsService } from './consents.service';
import { Store } from '@ngrx/store';
import { ICertificateData } from '../manage/manage';
import { selectorCertificate } from '../manage/manage.reducer';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { IStatusIcon } from '@app/shared/interfaces/common-interfaces';
import { RevokeConsentComponent } from './revoke-consent/revoke-consent.component';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { IConsentElement, ConsentRequest, consentStatusMap, ConsentStatus } from '@app/certificates/consents/consent';
import { InfoService } from '@app/core/info/info.service';
import { ActionProfilePlanGet } from '@app/profile/profile-remaining-count/profile-plan.actions';

@Component({
  selector: 'cloud-consents',
  templateUrl: './consents.component.html',
  styleUrls: ['./consents.component.scss'],
})
export class ConsentsComponent implements OnInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  readonly displayedColumns = [
    'status',
    'createdDate',
    'expirationDate',
    'orgName',
    'roleName',
    'actions'
  ];

  certs: ICertificateData[];
  consentStatusEnum = ConsentStatus;

  options: ConsentRequest = {
    certReqIds: [],
    offset: 0,
    limit: 10
  };
  consentsSize: any;

  unsubscribe$: Subject<void> = new Subject<void>();
  dataSource: MatTableDataSource<IConsentElement>;
  loading = true;
  signedfileName: string;

  constructor(
    private consentService: ConsentsService,
    private store: Store<any>,
    public tableHelper: MatTableHelper,
    public dialog: MatDialog,
    public info: InfoService) {
    this.store
      .select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(certs => this.certs = certs);
    this.store.dispatch(new ActionProfilePlanGet());
  }

  ngOnInit() {
    this.certs.forEach(cert => {
      this.options.certReqIds.push(cert.certReqId);
    });
    this.loadConsents();
  }

  private loadConsents() {
    this.consentService.getAll(this.options).subscribe(r => {
      if (r.data) {
        this.loading = false;
        const consents = r.data.consents;
        this.dataSource = new MatTableDataSource<IConsentElement>(consents);
        this.consentsSize = r.data.size;
        this.setPaginator();
      }
    });
  }

  private setPaginator() {
    this.paginator.pageIndex = this.options.offset;
    this.paginator.pageSize = this.options.limit;
    this.paginator.length = this.consentsSize;
  }

  onPaginateChange(paginator) {
    this.options.limit = paginator.pageSize;
    this.options.offset += (paginator.pageIndex - paginator.previousPageIndex);
    this.loadConsents();
  }

  filterByStatus() {
    if (this.options.status && this.options.status === ConsentStatus.ALL) {
      delete this.options.status;
    }
    this.options.offset = 0;
    this.loadConsents();
  }

  hasData() {
    return this.dataSource && this.dataSource.data.length !== 0;
  }

  statusIcon(element: IConsentElement): IStatusIcon {
    if (element) {
      return consentStatusMap[element.status].icon;
    }
  }

  statusText(status: ConsentStatus) {
    return consentStatusMap[status].text;
  }

  isActive(element: IConsentElement) {
    return element.status === ConsentStatus.ACTIVE;
  }

  isActivated(element: IConsentElement) {
    return element.status !== ConsentStatus.INACTIVATED;
  }

  // Actions
  revokeConsent(consent) {
    const dialogRef = this.dialog.open(RevokeConsentComponent, { data: consent });
    const sub = dialogRef.componentInstance.close.subscribe(revokeReason => {
      if (revokeReason) {
        consent.revokeReason = revokeReason;
        this.loadConsents();
      }
      this.dialog.closeAll();
    });
  }

  downloadConsent(element: IConsentElement) {
    this.consentService.getSignedConsentFile(element.consentId)
    .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (ConsentsComponent.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any) 
              if(nav.msSaveOrOpenBlob){
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }
  // End actions

  static isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

}
